import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { content, type BundledStage } from '../content/bundle';

function getStarRating(_stageId: string): number {
  return 0;
}

function StageNode({ stage, unlocked }: { stage: BundledStage; unlocked: boolean }) {
  const { t } = useTranslation();
  const stars = getStarRating(stage.id);
  const cleared = stars > 0;

  const node = (
    <div
      className={`
        border-2 p-2 text-center min-w-[56px] transition-opacity
        ${stage.boss ? 'border-neon-pink bg-ink-800' : 'border-ink-600 bg-ink-900'}
        ${!unlocked ? 'opacity-30 cursor-not-allowed' : 'hover:border-neon-cyan cursor-pointer'}
        ${cleared ? 'border-neon-yellow' : ''}
      `}
      data-testid={`stage-node-${stage.id}`}
    >
      <PixelText size="sm" color={stage.boss ? 'pink' : 'white'}>
        {stage.boss ? t('campaign.boss') : stage.id}
      </PixelText>
      <div className="text-neon-yellow text-xs mt-0.5">
        {Array.from({ length: 3 }, (_, i) => i < stars ? '★' : '☆').join('')}
      </div>
    </div>
  );

  if (!unlocked) return node;
  return <Link to={`/campaign/stage/${stage.id}`}>{node}</Link>;
}

function ChapterRow({ chapter }: { chapter: number }) {
  const { t } = useTranslation();
  const stages = content.getChapter(chapter);

  return (
    <PixelPanel>
      <PixelText size="base" color="cyan" className="mb-3 block">{t('campaign.chapter', { n: chapter })}</PixelText>
      <div className="flex flex-wrap gap-2">
        {stages.map((stage, idx) => (
          <StageNode
            key={stage.id}
            stage={stage}
            unlocked={chapter === 1 || idx < stages.length}
          />
        ))}
      </div>
    </PixelPanel>
  );
}

export function Campaign() {
  const { t } = useTranslation();
  const me = usePlayerStore(s => s.me);
  if (!me) return <Navigate to="/login" replace />;

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/stanwars/sprites/groups/stray_kids.png)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', imageRendering: 'pixelated', opacity: 0.12 }}
      />
      <div className="flex items-center gap-3 relative">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="pink">{t('campaign.title')}</PixelText>
      </div>

      <div className="flex flex-col gap-4 relative">
        {content.chapters().map(ch => (
          <ChapterRow key={ch} chapter={ch} />
        ))}
      </div>
    </main>
  );
}
